import { useState } from 'react'
import { AlertCircle, CalendarX, Search, RefreshCw } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Label,
} from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { useNoScheduleReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import type { NoScheduleReportFilters, NoScheduleRecord } from '../../types/reports.types'

interface Organization {
  id: string
  name: string
}

interface Department {
  id: string
  name: string
}

export function NoScheduleReport() {
  const [filters, setFilters] = useState<NoScheduleReportFilters>({})
  const [shouldFetch, setShouldFetch] = useState(false)

  const { data: organizations } = useOrganizationsForSelect() as { data: Organization[] | undefined }
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null) as { data: Department[] | undefined }

  const {
    data: report,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useNoScheduleReport(filters, shouldFetch)

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setFilters((prev) => ({
      ...prev,
      organizationId: value || undefined,
      departmentId: undefined,
    }))
  }

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setFilters((prev) => ({
      ...prev,
      departmentId: value || undefined,
    }))
  }

  const handleGenerateReport = () => {
    setShouldFetch(true)
    if (shouldFetch) {
      refetch()
    }
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* Organization select */}
            <div className="space-y-2">
              <Label htmlFor="organization">Организация</Label>
              <select
                id="organization"
                value={filters.organizationId || ''}
                onChange={handleOrganizationChange}
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Все организации</option>
                {organizations?.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Department select */}
            <div className="space-y-2">
              <Label htmlFor="department">Подразделение</Label>
              <select
                id="department"
                value={filters.departmentId || ''}
                onChange={handleDepartmentChange}
                disabled={!filters.organizationId}
                className="h-10 w-[250px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Все подразделения</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleGenerateReport}
                disabled={isLoading || isFetching}
                className="gap-2"
              >
                {isLoading || isFetching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Сформировать отчет
              </Button>
              <Button variant="default" className="bg-green-600 hover:bg-green-700" disabled>
                <span className="mr-2">📊</span>
                Экспорт в Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => setFilters({ organizationId: undefined, departmentId: undefined })}
              >
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчета. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {shouldFetch && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarX className="h-5 w-5" />
              Сотрудники без графика
            </CardTitle>
            <CardDescription>
              {report ? (
                <>
                  Без графика: {report.meta.total} из {report.meta.totalEmployees} сотрудников
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : report && report.data.length > 0 ? (
              <NoScheduleTable records={report.data} />
            ) : (
              <div className="text-center py-8">
                <CalendarX className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Все сотрудники имеют назначенный график
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Нет сотрудников без расписания в выбранных фильтрах.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial state - before report generation */}
      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <CalendarX className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Нажмите "Сформировать отчет"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Отчет покажет всех активных сотрудников, которым не назначен график работы.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Table component
function NoScheduleTable({ records }: { records: NoScheduleRecord[] }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>Сотрудник</TableHead>
            <TableHead>Таб. номер</TableHead>
            <TableHead>Организация</TableHead>
            <TableHead>Подразделение</TableHead>
            <TableHead>Должность</TableHead>
            <TableHead>Дата приёма</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record, index) => (
            <TableRow key={record.employee_id}>
              <TableCell className="font-medium">{index + 1}</TableCell>
              <TableCell className="font-medium">{record.employee_name}</TableCell>
              <TableCell>{record.employee_number || '-'}</TableCell>
              <TableCell>{record.organization_name || '-'}</TableCell>
              <TableCell>{record.department_name || '-'}</TableCell>
              <TableCell>{record.position_name || '-'}</TableCell>
              <TableCell>{record.hire_date || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
